import { inject, Injectable, computed, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { HttpClient, httpResource } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Season, SeasonSummary } from '../../../shared/models/season';
import { SeasonRecap } from '../../../shared/models/season-recap';
import { User } from '../../../shared/models/user';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root',
})
export class SeasonService {
  readonly #authService = inject(AuthenticationService);
  readonly #injector = inject(EnvironmentInjector);

  readonly currentSeasonResource = httpResource<Season>(() => {
    if (!this.#authService.authenticatedUserId()) {
      return undefined;
    }
    return environment.apiHost + 'seasons/current';
  });

  readonly seasonRewards = computed(() => this.currentSeasonResource.value()?.rewards ?? []);

  readonly accessibleSeasonsResource = httpResource<SeasonSummary[]>(() => {
    if (!this.#authService.authenticatedUserId()) {
      return undefined;
    }
    return environment.apiHost + 'seasons';
  });

  getCurrentSeasonResource() {
    return this.currentSeasonResource;
  }

  getAccessibleSeasonsResource() {
    return this.accessibleSeasonsResource;
  }

  getSeasonDetailsResource(seasonId: () => string | undefined | null) {
    return runInInjectionContext(this.#injector, () =>
      httpResource<Season>(() => {
        if (!this.#authService.authenticatedUserId()) {
          return undefined;
        }
        const id = seasonId();
        return id ? `${environment.apiHost}seasons/${id}` : undefined;
      })
    );
  }

  getSeasonRankingsResource(seasonId: () => string | undefined | null) {
    return runInInjectionContext(this.#injector, () =>
      httpResource<User[]>(() => {
        if (!this.#authService.authenticatedUserId()) {
          return undefined;
        }
        const id = seasonId();
        return id ? `${environment.apiHost}seasons/${id}/rankings` : undefined;
      })
    );
  }

  getSeasonUserRankingResource(seasonId: () => string | undefined | null) {
    return runInInjectionContext(this.#injector, () =>
      httpResource<number>(() => {
        if (!this.#authService.authenticatedUserId()) {
          return undefined;
        }
        const id = seasonId();
        return id ? `${environment.apiHost}seasons/${id}/rankings/me` : undefined;
      })
    );
  }

  getSeasonRecapResource(seasonId: () => string | undefined | null) {
    return runInInjectionContext(this.#injector, () =>
      httpResource<SeasonRecap>(() => {
        if (!this.#authService.authenticatedUserId()) {
          return undefined;
        }
        const id = seasonId();
        return id ? `${environment.apiHost}seasons/${id}/recap` : undefined;
      })
    );
  }
}
